
// NOTE: Filling the screen completely needs to solve the 0-1 Knapsack Problem or the subset-sum problem
//  - Seems to be specifically "max sum multiple subset sum problem"
// This does NOT currently try to solve this problem.

// @ts-check

/**
 * @typedef {import('./coreTypes').ResultAtom} ResultAtom
 * @typedef {import('./coreTypes').JSResultAtom} JSResultAtom
 * @typedef {import('./coreTypes').QuerySpec} QuerySpec
 */


/**
 * @typedef {{ next: () => number, recover: (a: number) => number, reset: () => void }} LayoutAgent
 * @typedef {(totalNumOfCards: number, numOfColumns: number) => LayoutAgent} LayoutAgentBuilder
 */

import parseDonors from "./ResultAtomParsingFunctions";


// TODO: Figure out how to get the proper values for these:

const nameFont = 'Arial';
const nameFontSize = '24px';
const nameFontWeight = 'bold';

const campaignFont = 'Arial';
const campaignFontSize = '16px';
const campaignFontWeight = 'normal';

/**
 * @param {string} font 
 * @param {string} fontSize 
 * @param {string} fontWeight 
 * @returns {function(string): number}
 */
function generateTextWidthMeasurer(font, fontSize, fontWeight) {
	const canvas = document.createElement('canvas');
	canvas.style.font = font;
	canvas.style.fontSize = fontSize;
	canvas.style.fontWeight = fontWeight;
	const context = canvas.getContext('2d');
	if (context === null) throw new Error('Text width context was null');
	return text => context.measureText(text).width;
}

// This memoizes the specific objects for performance.

/**
 * @type {function(string): number}
 */
const nameTextMeasurer = (function() {
	const m = generateTextWidthMeasurer(nameFont, nameFontSize, nameFontWeight);
	return text => m(text);
})();

/**
 * @type {function(string): number}
 */
const campaignTextMeasurer = (function() {
	const m = generateTextWidthMeasurer(campaignFont, campaignFontSize, campaignFontWeight);
	return text => m(text);
})();



/**
 * 
 * @param {number} screenSizePixels 
 * @param {number} numOfColumns 
 * @param {LayoutAgentBuilder} layoutAgentBuilder 
 * @returns {function(ResultAtom[]): [ResultAtom, number][][][]} lists of results returning a list of columns of pages
 */
function paginator(screenSizePixels, numOfColumns, layoutAgentBuilder)
{
	//console.log("In paginator"); 
	
	/**
	 * @param {number} p 
	 * @returns number
	 */
	const pixelsToBlocks = function(p) {
		return Math.floor(p / 100);
	}
	
	const bottomTolerance = (screenSizePixels >= 1100) ? 2 : 1;
	const screenSizeBlocks = pixelsToBlocks(screenSizePixels) - bottomTolerance;
	
	/**
	 * @param {ResultAtom[]} cards
	 */
	return function(cards) 
	{
		//console.log("In paginator return function");
		
		const totalNumOfCards = cards.length;
		const layoutAgent = layoutAgentBuilder(totalNumOfCards, numOfColumns);
		
		/**
		 * @type [ResultAtom, number][]
		 */
		const cardsAndBlocks = cards.map(c => [c, donorCardToBlocks(c)]);
		
		const buildEmptyCols = () => {
			const cols = [];
			for (let a = 0; a < numOfColumns; a++) { cols.push([]); }
			return cols;
		}
		
		//console.log("ScreenSizeBlocks: " + screenSizeBlocks);
		
		const pages = [];
		
		/**
		 * @type {[ResultAtom, number][][]}
		 */
		let page = [];
		/**
		 * @type {number[]}
		 */
		let colsFullness = [];
		
		const initPage = () => {
			page = buildEmptyCols();
			colsFullness = buildEmptyCols().map(c => 0);
			layoutAgent.reset();
		};
		
		initPage();
		
		for (let a = 0; a < cardsAndBlocks.length; a++) {
			
			// Ignore undisplayable pathological cards, to prevent an infinite loop later.
			if (cardsAndBlocks[a][1] > screenSizeBlocks) continue;
				
			// dummy loop for goto functionality.
			currentCardProcessing:
			for (;;) {

				const nextCol = layoutAgent.next();
				
				 // console.log("DEBUG: current card: ", cardsAndBlocks[a]);
				 // console.log("DEBUG: nextCol: ", nextCol);
				 // console.log("DEBUG: colsFullness: ", colsFullness);
				
				// column space is too small
				if (colsFullness[nextCol] + cardsAndBlocks[a][1] > screenSizeBlocks) {
					
					let colsFailed = 1;
					let recovCol = layoutAgent.recover(nextCol);
					
					// looking for two conditions:
					for (;;) {
						
						// knowing no column can hold the card, so that a new page is needed.
						if (colsFailed >= numOfColumns) {
							// console.log("All columns failed, advancing page.");
							pages.push(page);
							initPage();
							// don't advance to the next card since we haven't placed it yet.
							continue currentCardProcessing;
						}
						
						// if a column that can hold the card...
						else if (colsFullness[recovCol] + cardsAndBlocks[a][1] <= screenSizeBlocks) {
							// console.log("Column " + recovCol + " works.");
							
							page[recovCol].push(cardsAndBlocks[a]);
							colsFullness[recovCol] += cardsAndBlocks[a][1];
							break;
						}
						
						else {
							recovCol = layoutAgent.recover(recovCol);
							colsFailed++;
						}
					}
					
				}
				else { // column can fit card
					page[nextCol].push(cardsAndBlocks[a]);
					colsFullness[nextCol] += cardsAndBlocks[a][1];
				}
			
				break;
			}
		
		}
		
		pages.push(page);
		return pages;
	}
}


function isEmpty(a) {
	return (a === undefined || a === null || (Array.isArray(a) && a.length === 0));
}


/**
 * @param {ResultAtom} donorCardResultAtom 
 * @returns {number}
 */
function donorCardToBlocks(donorCardResultAtom)
{
	const [tag, dc] = donorCardResultAtom;
	
    if (tag === 'donor' || tag === 'donor_legacy') {
	
		// Empirical derivations; MAY CHANGE IF VISUALS CHANGE.
		//--------------------------------------------------------
		
		const nameLineWidth = 150; //px;
		const campaignLineWidth = 175; //px;
		
		/**
		 * @type {function(number): number}
		 */
		const nameLinesToBlocks = function(l) {
			if (l === 1) return 1;
			else if (l <= 4) return 2;
			else if (l <= 8) return 3;
			else if (l === 9) return 4;
			else return Math.ceil(l/2);
		};
	
		
		const campaignLinesToBlocks = function(l, t) {
			if (l <= 2)  {
				if (l === 1 && t === 'donor_legacy') return 0;
				else return 1;
			}
			else if (l <= 3) return 1;
			else if (l <= 7) return 2;
			else return Math.ceil(l/2);
		};

		/**
		 * @type {function(number, number, number): number}
		 */
		const commentLinesToBlocks = (nameBlocks, campaignBlocks, l) => {
			if (campaignBlocks >= 1) return Math.max(Math.ceil(l/3) - 1, 0);
			else return Math.ceil(l/3);
		};

		//----------------------------------------------------------
		
		// figure out height cost of name:
		const nameLength = nameTextMeasurer(dc['first_name'] + ' ' + dc['last_name']);
		const nameLines = Math.ceil(nameLength / nameLineWidth);
		
		const nameBlocks = nameLinesToBlocks(nameLines);
		
		// if no campaigns, no extra cost than name cost
		if (isEmpty(dc['campaigns'])) return nameBlocks;
		
		let campaignLines = 0;

		if (dc['campaigns'] !== null) {
			// there are campaigns:
			campaignLines = 
				dc['campaigns'].map(c => Math.ceil(campaignTextMeasurer(c) / campaignLineWidth)).reduce((a,b) => a + b, 0);
		}
		
		const campaignBlocks = campaignLinesToBlocks(campaignLines, tag);
		
		const campaignCommentLines = 
			Object.entries(dc['campaign_comments']).map(i => i[1]).flatMap(i => i).length;

		const campaignCommentBlocks = commentLinesToBlocks(nameBlocks, campaignBlocks, campaignCommentLines);

		return nameBlocks + campaignBlocks + campaignCommentBlocks;
	}
	// unknown
	else throw new Error("Invalid donor block tag.");
}



/**
 * @param {ResultAtom[]} resultAtoms 
 * @returns {JSResultAtom[]}
 */
 function resultAtomsToParsedDonors(resultAtoms) {

    /** @type {[ResultAtom, number | null][]} */
    const cardsAndBlockSizes = 
        resultAtoms.map(c => [c, donorCardToBlocks(c)]);
    
    const cardsAndBlockSizesNoNulls = 
    /** @type {[ResultAtom, number][]} */
        (cardsAndBlockSizes.filter(p => p[1] !== null));

    return parseDonors(cardsAndBlockSizesNoNulls);
}


/**
 * @param {number} totalNumOfCards 
 * @param {number} numOfColumns 
 * @returns {LayoutAgent}
 */
function downLayoutAgentBuilder(totalNumOfCards, numOfColumns)
{
	const getNextCol = col => {
		if (col >= numOfColumns-1) return 0;
		else return col+1;
	};
	
	let colIndex = 0;
	
	return {
		next: () => { return colIndex; },
		recover: column => { colIndex = getNextCol(column); return colIndex; },
		reset: () => { colIndex = 0; }
	};
}

export { paginator, downLayoutAgentBuilder, donorCardToBlocks, resultAtomsToParsedDonors };